import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import CountryService from '../../../Api/services/country.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type CountryResponse = components['schemas']['CountryResponse'];
type CreateCountry = components['schemas']['CreateCountry'];
type UpdateCountry = components['schemas']['UpdateCountry'];

interface CountryFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  country: CountryResponse | null;
  isEditing: boolean;
  onSuccess: () => void;
}

const CountryFormModal: React.FC<CountryFormModalProps> = ({
  isOpen,
  toggle,
  country,
  isEditing,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateCountry>>({
    name: '',
    code: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (isEditing && country) {
        setFormData({
          name: country.name || '',
          code: country.code || '',
        });
      } else {
        setFormData({
          name: '',
          code: '',
        });
      }
    }
  }, [isOpen, country, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.name.trim()) {
      toast.error(t('El nombre es requerido'));
      return;
    }

    if (!formData.code || !formData.code.trim()) {
      toast.error(t('El código es requerido'));
      return;
    }

    try {
      setLoading(true);
      if (isEditing && country?.id) {
        const updateData: UpdateCountry = {
          id: country.id,
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
        };
        await CountryService.updateCountry(updateData);
        toast.success(t('País actualizado exitosamente'));
      } else {
        const createData: CreateCountry = {
          name: formData.name.trim(),
          code: formData.code.trim().toUpperCase(),
        };
        await CountryService.createCountry(createData);
        toast.success(t('País creado exitosamente'));
      }
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al guardar país');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isEditing ? t('Editar País') : t('Nuevo País')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Nombre')} *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('Nombre del país')}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>{t('Código')} *</Label>
            <Input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder={t('Código del país (ej: US, MX, ES)')}
              maxLength={3}
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Guardando...')}
              </>
            ) : (
              t('Aceptar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CountryFormModal;

